import { typedEnv } from "@/config/typedEnv"

/**
 * The platform's own shopper stand for this environment. The demo token minted
 * for a merchant is allowlisted for every stand server-side (platform-origin
 * wildcard), so the same token works no matter which one a build points at.
 */
export const VIEWER3D_DEMO_STAND_URL =
  typedEnv.VITE_VIEWER3D_DEMO_URL ?? "https://dev.tintvto.com/3d-viewer"

/**
 * A merchant-facing token plus the embed attribute it belongs in. Both resolve
 * the same product server-side, but only `variant-id` also tells the viewer
 * WHICH variant to open — on `product-id` it opens the first one.
 */
export type Viewer3DDemoTarget = {
  param: "product-id" | "variant-id"
  token: string
}

/** Builds a stand URL for the 3D viewer. A null target demos the merchant's
 * first published product. */
export const buildViewer3DDemoUrl = (token: string, target: Viewer3DDemoTarget | null): string => {
  const url = new URL(VIEWER3D_DEMO_STAND_URL.replace(/\/+$/, "") + "/")
  url.searchParams.set("token", token)
  if (target) url.searchParams.set(target.param, target.token)
  // Local stands can't read their api-base from a CDN config.json — point the
  // widget at the configured public viewer API explicitly.
  if (typedEnv.VITE_VIEWER3D_DEMO_API_BASE)
    url.searchParams.set("api-base", typedEnv.VITE_VIEWER3D_DEMO_API_BASE)
  return url.toString()
}

/**
 * A product can only be demoed once it is published (the public catalog serves
 * published records) and carries a merchant-facing id the viewer resolves by.
 * That is the Merchant Variant ID of its first showable variant: the editor no
 * longer asks for a product-level Product ID, so on anything created after the
 * variant-first change `externalId` is empty. Legacy rows that still carry one
 * fall back to it — as `product-id`, because a product-level id is not a
 * variant and would preselect nothing.
 */
export const resolveViewer3DDemoTarget = (product: {
  demoExternalId?: string | null
  externalId?: string | null
  published?: boolean
}): Viewer3DDemoTarget | null => {
  if (!product.published) return null

  const variantToken = product.demoExternalId?.trim()
  if (variantToken) return { param: "variant-id", token: variantToken }

  const productToken = product.externalId?.trim()
  return productToken ? { param: "product-id", token: productToken } : null
}
